import React from "react";
import { BookOpen } from "lucide-react";
import { getAccentColor, useAccentColor } from "utils/accent-color";

// Over a banner image the badge has to stay white-on-glass to be legible, so
// the chapter's colour_code is carried by a dot instead of the text itself —
// tuned against a dark backdrop rather than the current theme's surface.
const ChapterBadge = ({ kb, accent, onImageAccent, onImage }) =>
  kb && (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${
        onImage ? "bg-white/15 backdrop-blur-sm text-white border-white/30" : ""
      }`}
      style={onImage ? undefined : { backgroundColor: accent.fill, color: accent.text, borderColor: accent.border }}
    >
      {onImage ? (
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: onImageAccent.text }}
        />
      ) : (
        <BookOpen size={12} />
      )}
      {kb.title}
    </span>
  );

const NewBadge = () => (
  <span className="px-2.5 py-1 rounded-md text-[10.5px] font-bold uppercase tracking-wide bg-danger text-white">
    New
  </span>
);

// Shared hero block — chapter badge, "New" badge, title, and excerpt — for
// anywhere an article's banner is shown. Used by both the client reader
// page (ArticleHeader) and the admin preview dialog, so the preview can
// never visually drift from what readers actually see.
const ArticleHero = ({ article }) => {
  const kb = article.knowledge_base;
  const accent = useAccentColor(kb?.color_code);
  const onImageAccent = getAccentColor(kb?.color_code, true);

  if (article.banner_image) {
    return (
      <div className="relative w-full h-64 sm:h-96">
        <img
          src={article.banner_image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-3xl mx-auto w-full px-4 sm:px-6 pb-5 sm:pb-7">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <ChapterBadge kb={kb} accent={accent} onImageAccent={onImageAccent} onImage />
            {article.is_new && <NewBadge />}
          </div>
          <h1 className="text-[24px] sm:text-[32px] font-bold text-white leading-tight tracking-tight [text-wrap:balance]">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-2 text-[13.5px] sm:text-[15px] text-white/80 leading-relaxed line-clamp-2">
              {article.excerpt}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <ChapterBadge kb={kb} accent={accent} />
        {article.is_new && <NewBadge />}
      </div>
      <h1 className="text-[26px] sm:text-[34px] font-bold text-text_primary leading-tight tracking-tight">
        {article.title}
      </h1>
      {article.excerpt && (
        <p className="mt-3 text-[15px] sm:text-base text-text_secondary leading-relaxed">{article.excerpt}</p>
      )}
    </div>
  );
};

export default ArticleHero;
